import type {
  ConnectionSettings,
  SupportedSyncToTimelyEnums,
  SyncSettingConfig,
} from '@timelyapp/integration-cloud-api-spec';
import { SYNC_PROJECT_OPTION_SETTING_KEYS } from '@timelyapp/integration-cloud-api-spec';

type SyncSettingKey = SyncSettingConfig['key'];

/**
 * Centralized processor for sync settings from operations
 * This is the single source of truth for all sync settings logic
 */

// Extend the backend SyncSettingConfig type with runtime state
export type SyncSettingsProps = SyncSettingConfig & {
  enabled: boolean;
  visibleToEndUser: boolean;
  internalOnly: boolean;
};

export type SyncSettingsReturns = {
  allSettings: SyncSettingsProps[];
  visibleSettings: SyncSettingsProps[];
  settingsByKey: Map<SyncSettingKey, SyncSettingsProps>;
  getDependencyTitles: (setting: SyncSettingsProps) => string[];
  checkDependenciesSatisfied: (setting: SyncSettingsProps) => boolean;
};

export const LEGACY_SYNC_SETTING_KEYS: ReadonlyArray<SupportedSyncToTimelyEnums> = [
  'sync_projects',
  'sync_tasks',
  'reference_tasks',
  'sync_tags',
  'sync_clients',
  'sync_teams',
  'sync_users',
  'sync_memories',
  'sync_time_entries',
] as const;

export const PROJECT_SYNC_SETTING_KEYS = SYNC_PROJECT_OPTION_SETTING_KEYS;

export const LEGACY_SYNC_SETTING_LABELS: Record<SupportedSyncToTimelyEnums, string> = {
  sync_projects: 'Projects',
  sync_tasks: 'Tasks',
  reference_tasks: 'Linked Work',
  sync_tags: 'Tags',
  sync_clients: 'Clients',
  sync_teams: 'Teams',
  sync_users: 'Users',
  sync_memories: 'Memories',
  sync_time_entries: 'Time Entry Sync',
};

export function getSyncSettingBooleanValue(
  settings: Partial<ConnectionSettings> | Record<string, any>,
  key: SyncSettingKey
): boolean | undefined {
  if (typeof key !== 'string') return undefined;

  if (LEGACY_SYNC_SETTING_KEYS.includes(key as SupportedSyncToTimelyEnums)) {
    const value = (settings as any)?.[key];
    return typeof value === 'boolean' ? value : undefined;
  }

  if (PROJECT_SYNC_SETTING_KEYS.includes(key as any)) {
    const value = (settings as any)?.[key];
    return typeof value === 'boolean' ? value : undefined;
  }

  return undefined;
}
/**
 * Main processor function - extracts and processes a provider's sync settings.
 * This is the single source of truth for sync settings processing.
 *
 * Settings are a flat, account-scoped list resolved server-side from either a fan-out
 * framework provider's top-level `settings` or a legacy provider's operation-embedded
 * settings (see the integration-details query's `resolveSyncSettings`). The per-setting
 * enabled state is derived by combining each declaration's `defaultValue` with the
 * connection's saved `settings`.
 */
// Type for connection settings as returned by the API (with nullable booleans)

export function processSyncSettings(
  settings: SyncSettingConfig[],
  connectionSettings: Partial<ConnectionSettings> | Record<string, any>
): SyncSettingsReturns {
  const allSettings: SyncSettingsProps[] = [];
  const settingsByKey = new Map<SyncSettingKey, SyncSettingsProps>();

  settings.forEach(setting => {
    const visibleToEndUser = setting.visibleToEndUser ?? true;
    const internalOnly = setting.internalOnly ?? false;
    const processed: SyncSettingsProps = {
      ...setting, // Spread all properties from SyncSettingConfig
      enabled:
        getSyncSettingBooleanValue(connectionSettings, setting.key) ??
        setting.defaultValue ??
        false,
      visibleToEndUser,
      internalOnly,
    };
    allSettings.push(processed);
    settingsByKey.set(setting.key, processed);
  });

  // Helper function to get dependency titles
  const getDependencyTitles = (setting: SyncSettingsProps): string[] => {
    if (!setting.dependant_on || setting.dependant_on.length === 0) return [];

    return setting.dependant_on.map(depKey => {
      const depSetting = settingsByKey.get(depKey);
      return depSetting?.label || depKey;
    });
  };

  // Helper function to check if dependencies are satisfied
  const checkDependenciesSatisfied = (setting: SyncSettingsProps): boolean => {
    if (!setting.dependant_on || setting.dependant_on.length === 0) return true;

    return setting.dependant_on.every(depKey => {
      const depSetting = settingsByKey.get(depKey);
      return depSetting?.enabled ?? false;
    });
  };

  const visibleSettings = allSettings.filter(setting => setting.visibleToEndUser !== false);

  return {
    allSettings,
    visibleSettings,
    settingsByKey,
    getDependencyTitles,
    checkDependenciesSatisfied,
  };
}

/**
 * Get settings sorted by priority for display
 */
export function getSortedSettings(processedSettings: SyncSettingsReturns): SyncSettingsProps[] {
  return [...processedSettings.visibleSettings].sort((a, b) => {
    // Sort by priority first
    const aPriority = getSyncSettingPriorityValue(a.key);
    const bPriority = getSyncSettingPriorityValue(b.key);

    if (aPriority !== bPriority) {
      return aPriority - bPriority;
    }

    // Then sort alphabetically (use key as fallback if label is undefined)
    const aLabel = a.label || a.key;
    const bLabel = b.label || b.key;
    return aLabel.localeCompare(bLabel);
  });
}

/**
 * Determines display priority for sync setting types.
 * @param syncType - The sync setting key (e.g., 'sync_projects', 'sync_tasks')
 * @returns Priority level for UI ordering
 */
export function getSyncSettingPriority(
  syncType: SyncSettingKey
): 'primary' | 'secondary' | 'tertiary' {
  if (PROJECT_SYNC_SETTING_KEYS.includes(syncType as any)) return 'tertiary';
  if (['sync_projects', 'reference_tasks', 'sync_memories'].includes(syncType as string))
    return 'primary';
  if (['sync_tags', 'sync_clients', 'sync_teams', 'sync_users'].includes(syncType as string))
    return 'secondary';
  return 'tertiary';
}

/**
 * Get numeric priority value for sorting operations
 * @param syncType - The sync setting key
 * @returns Numeric priority (1 = highest, 3 = lowest)
 */
export function getSyncSettingPriorityValue(syncType: SyncSettingKey): number {
  const priority = getSyncSettingPriority(syncType);
  return priority === 'primary' ? 1 : priority === 'secondary' ? 2 : 3;
}

/**
 * Check if any operation's settings are enabled
 */
export function hasEnabledSettings(processedSettings: SyncSettingsReturns): boolean {
  return processedSettings.allSettings.some((setting: SyncSettingsProps) => setting.enabled);
}

export function hasOperationsBasedSettings(processedSettings: SyncSettingsReturns): boolean {
  return processedSettings.allSettings.length > 0;
}

/**
 * Get all settings that need to be auto-enabled based on dependencies
 */
export function getRequiredDependencies(
  processedSettings: SyncSettingsReturns,
  settingKey: SupportedSyncToTimelyEnums
): SupportedSyncToTimelyEnums[] {
  const setting = processedSettings.settingsByKey.get(settingKey);
  if (!setting?.dependant_on) return [];

  // Recursively get all dependencies
  const allDeps = new Set<SupportedSyncToTimelyEnums>();
  const toProcess = [...setting.dependant_on];

  while (toProcess.length > 0) {
    const depKey = toProcess.pop()!;
    if (!allDeps.has(depKey)) {
      allDeps.add(depKey);
      const depSetting = processedSettings.settingsByKey.get(depKey);
      if (depSetting?.dependant_on) {
        toProcess.push(...depSetting.dependant_on);
      }
    }
  }

  return Array.from(allDeps);
}
